import { Pipe, PipeTransform } from '@angular/core';
import {
  TimeString,
  format12Hour,
  format24Hour,
  isValidTimeString
} from '../../helpers';

export type TimeStringFormat = '24h' | '12h';

/**
 * Pipe to format TimeString (HH:MM:SS) for display
 *
 * @example
 * ```html
 * <!-- Default: 24h format without seconds -->
 * {{ '23:55:00' | timeString }}  <!-- Output: 23:55 -->
 *
 * <!-- 12h format with AM/PM -->
 * {{ '23:55:00' | timeString:'12h' }}  <!-- Output: 11:55 PM -->
 * {{ '11:30:00' | timeString:'12h' }}  <!-- Output: 11:30 AM -->
 * {{ '00:15:00' | timeString:'12h' }}  <!-- Output: 12:15 AM -->
 * ```
 */
@Pipe({ name: 'timeString', standalone: true })
export class TimeStringPipe implements PipeTransform {
  /**
   * Transforms a TimeString into a formatted display string
   *
   * @param value - TimeString in HH:MM:SS format
   * @param format - '24h' (default) or '12h' for AM/PM format
   * @returns Formatted time string (HH:MM or hh:mm AM/PM)
   */
  transform(
    value: TimeString | string | null | undefined,
    format: TimeStringFormat = '24h'
  ): string {
    if (!value) return '';

    // Validate the time string
    if (!isValidTimeString(value)) return value;

    if (format === '12h') return format12Hour(value);

    return format24Hour(value);
  }
}
